import type { ContextField } from "@appretio/context-selector";

/** The 12 context fields shared by the forecast, forecast-summary, and manage-cubes pages. */
export const FORECAST_CONTEXT_FIELDS: ContextField[] = [
  "brand",
  "sub_brand",
  "product_line",
  "collection_type",
  "product_category",
  "chain",
  "sales_location",
  "stock_location",
  "country",
  "channel",
  "collection_season_name",
  "markdown_status"
];

/** Label metadata for each forecast context field, used by manage-cubes operations. */
export const FORECAST_CONTEXT_FIELD_LABELS = [
  { key: "brand", label: "Brand" },
  { key: "sub_brand", label: "Sub Brand" },
  { key: "product_line", label: "Product Line" },
  { key: "collection_type", label: "Collection Type" },
  { key: "product_category", label: "Product Category" },
  { key: "chain", label: "Chain" },
  { key: "sales_location", label: "Sales Location" },
  { key: "stock_location", label: "Stock Location" },
  { key: "channel", label: "Channel" },
  { key: "country", label: "Country" },
  { key: "collection_season_name", label: "Seasonality" },
  { key: "markdown_status", label: "Markdown Status" }
] as const;

/** Union of the 12 forecast context field key strings. */
export type ForecastContextFieldKey =
  (typeof FORECAST_CONTEXT_FIELD_LABELS)[number]["key"];

/** Return the human-readable label for a forecast context field key. */
export function getFieldLabel(key: string): string {
  return FORECAST_CONTEXT_FIELD_LABELS.find((f) => f.key === key)?.label ?? key;
}

/** Dimension options for summary table/row selectors and graph grouping. Derived from FORECAST_CONTEXT_FIELD_LABELS. */
export const FORECAST_CONTEXT_DIMENSIONS = [
  ...FORECAST_CONTEXT_FIELD_LABELS.map((f) => ({
    label: f.label,
    value: f.key
  })),
  { label: "Store Status", value: "store_status" }
];

/** Allowed dimension values for manage-cubes add / rename operations. */
export const DIMENSION_VALUE_PATTERN =
  /^[a-z0-9&.,\-_()/!#:+' àáâäåæçèéêëîíìñòóôöøùúûüýßışì´–]+$/;

/** What DIMENSION_VALUE_PATTERN allows (mirrors backend BRAND_NAME_ERROR). */
export const DIMENSION_VALUE_RULES =
  "Only lowercase letters (including accented), numbers, and common " +
  "punctuation (&.,-()/!'_#:+) are allowed.";
